/**
 * Plan picker options for the In-Shift and End-of-Shift reports.
 *
 * The reports are always rendered against one plan. Until now that plan was
 * whatever baseline the backend resolved, with no way to look at a different
 * one. These helpers turn a raw `getAllShiftPlans` payload into the grouped,
 * ordered option list the picker renders:
 *
 *   1. Manual plans (operator-authored, PDP or SOS) — kept at the top because
 *      that is what the operator on shift actually ran to.
 *   2. RCC auto-generated plans (authored by ShiftPlanScheduler).
 *   3. Pending approval — listed separately, below the approved plans.
 *
 * Plans that have no reportable output (still collecting input, failed,
 * cancelled, rejected) are dropped.
 */
import { SPMSGetShiftPlanResponse } from '../../../../types/shiftPlan';
import { PlanStatus } from '../../../../utils/constants';
import { convertUTCToLocalTime } from '../../../../utils/string-utils';
import { isBaselinePlan } from '../../../shiftPlanning/CurrentPlanSummary/v2/baselinePlan';

/** Statuses whose plans have a published/finalised output worth reporting on. */
const APPROVED_STATUSES: string[] = [PlanStatus.APPROVED, PlanStatus.CLOSED];

/** Group keys, in the order they are rendered. */
export const PLAN_GROUP = {
  MANUAL: 'manual',
  AUTO: 'auto',
  PENDING_APPROVAL: 'pendingApproval',
} as const;

export type PlanGroupKey = (typeof PLAN_GROUP)[keyof typeof PLAN_GROUP];

/** Human-readable group headers shown in the dropdown. */
export const PLAN_GROUP_LABEL: Record<PlanGroupKey, string> = {
  [PLAN_GROUP.MANUAL]: 'Manual plans (PDP / SOS)',
  [PLAN_GROUP.AUTO]: 'RCC Recommended',
  [PLAN_GROUP.PENDING_APPROVAL]: 'Pending approval',
};

/**
 * Shown in place of the owner alias on RCC-generated plans. The backend records
 * these against the system user (`ShiftPlanScheduler`), which means nothing to an
 * operator reading the picker.
 */
export const RCC_CREATED_BY_LABEL = 'RCC Recommended';

export interface ReportPlanOption {
  /** Plan id — the option value. */
  planId: string;
  /** `planId · timestamp · createdBy`, as requested for the picker. */
  label: string;
  /** Local-time plan timestamp, or empty when the payload carries none. */
  timestamp: string;
  /** Owning alias (`lastUpdatedBy`, falling back to `createdBy`). */
  createdBy: string;
  /** `PDP` / `SOS` / `Intra-day`, or empty when `planType` is absent. */
  planTypeLabel: string;
  group: PlanGroupKey;
}

export interface ReportPlanGroup {
  key: PlanGroupKey;
  label: string;
  options: ReportPlanOption[];
}

const asString = (value?: string | boolean | number): string =>
  typeof value === 'string' ? value.trim() : '';

/** Plan status, tolerating both field spellings seen on list payloads. */
const getStatus = (plan: SPMSGetShiftPlanResponse): string =>
  asString(plan.planStatus) || asString(plan.status);

/** Timestamp, tolerating the fields seen on list payloads. */
const getRawTimestamp = (plan: SPMSGetShiftPlanResponse): string =>
  asString(plan.updatedAt) || asString(plan.lastModifiedDate) || asString(plan.createdDate);

/**
 * Who the picker credits the plan to. Operator plans show their alias; RCC
 * plans show "RCC Recommended" rather than the ShiftPlanScheduler system user.
 * Checked via isBaselinePlan, not the group, so a *pending-approval* RCC plan
 * (which lands in the pendingApproval group) is labelled the same way.
 */
const getCreatedBy = (plan: SPMSGetShiftPlanResponse): string =>
  isBaselinePlan(plan)
    ? RCC_CREATED_BY_LABEL
    : asString(plan.lastUpdatedBy) || asString(plan.createdBy);

/**
 * PDP / SOS label. `planType` is not guaranteed to be on every list payload, so
 * an unknown type yields an empty label rather than a wrong one.
 */
export const getPlanTypeLabel = (plan: SPMSGetShiftPlanResponse): string => {
  switch (asString(plan.planType)) {
    case 'NEXT_DAY':
      return 'PDP';
    case 'PRE_SHIFT':
      return 'SOS';
    case 'INTRA_DAY':
      return 'Intra-day';
    default:
      return '';
  }
};

/** Sort key: newest first. Unparseable timestamps sort last. */
const timeOf = (plan: SPMSGetShiftPlanResponse): number => {
  const raw = getRawTimestamp(plan);
  if (!raw) {
    return Number.NEGATIVE_INFINITY;
  }
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const toOption = (plan: SPMSGetShiftPlanResponse, group: PlanGroupKey): ReportPlanOption => {
  const planId = asString(plan.planId);
  const timestamp = convertUTCToLocalTime(getRawTimestamp(plan) || undefined);
  const createdBy = getCreatedBy(plan);
  const planTypeLabel = getPlanTypeLabel(plan);

  // Only join the parts that are actually present so the label never reads
  // "PLAN-1 ·  · " on a sparse payload.
  const label = [planTypeLabel, planId, timestamp, createdBy].filter(Boolean).join(' · ');

  return { planId, label, timestamp, createdBy, planTypeLabel, group };
};

/**
 * Groups plans for the report picker. Plans without a plan id, and plans with no
 * reportable output, are excluded.
 */
export const buildReportPlanGroups = (
  plans?: SPMSGetShiftPlanResponse[]
): ReportPlanGroup[] => {
  const reportable = (plans ?? []).filter((plan) => {
    if (!asString(plan.planId)) {
      return false;
    }
    const status = getStatus(plan);
    return APPROVED_STATUSES.includes(status) || status === PlanStatus.PENDING_APPROVAL;
  });

  const byGroup: Record<PlanGroupKey, SPMSGetShiftPlanResponse[]> = {
    [PLAN_GROUP.MANUAL]: [],
    [PLAN_GROUP.AUTO]: [],
    [PLAN_GROUP.PENDING_APPROVAL]: [],
  };

  reportable.forEach((plan) => {
    if (getStatus(plan) === PlanStatus.PENDING_APPROVAL) {
      byGroup[PLAN_GROUP.PENDING_APPROVAL].push(plan);
    } else if (isBaselinePlan(plan)) {
      byGroup[PLAN_GROUP.AUTO].push(plan);
    } else {
      byGroup[PLAN_GROUP.MANUAL].push(plan);
    }
  });

  // Within a group, newest plan first. Dedupe by plan id (a plan can appear in
  // both the day and night fetch on the EOS report, which merges the two).
  return [PLAN_GROUP.MANUAL, PLAN_GROUP.AUTO, PLAN_GROUP.PENDING_APPROVAL]
    .map((key) => {
      const seen = new Set<string>();
      const options = byGroup[key]
        .slice()
        .sort((a, b) => timeOf(b) - timeOf(a))
        .map((plan) => toOption(plan, key))
        .filter((option) => {
          if (seen.has(option.planId)) {
            return false;
          }
          seen.add(option.planId);
          return true;
        });
      return { key, label: PLAN_GROUP_LABEL[key], options };
    })
    .filter((group) => group.options.length > 0);
};

/** Flattened options in render order. */
export const flattenReportPlanGroups = (groups: ReportPlanGroup[]): ReportPlanOption[] =>
  // reduce rather than flatMap: the build targets a lib without Array.flatMap.
  groups.reduce<ReportPlanOption[]>((all, group) => all.concat(group.options), []);

/**
 * Default selection: prefer the plan the backend already resolved as the report
 * baseline, so opening the picker does not silently change what is on screen.
 * Otherwise fall back to the first option in render order (newest manual plan).
 */
export const resolveDefaultPlanId = (
  groups: ReportPlanGroup[],
  baselineShiftPlanId?: string
): string | null => {
  const options = flattenReportPlanGroups(groups);
  if (options.length === 0) {
    return null;
  }
  if (baselineShiftPlanId && options.some((option) => option.planId === baselineShiftPlanId)) {
    return baselineShiftPlanId;
  }
  return options[0].planId;
};
